/**
 * RegionalShiftBoard (BON-240) — the Shift board tab of the Teachers bucket.
 * Explicitly published open shifts only; unstaffed workshops never
 * auto-appear (that derived list stays on the Roster tab). Publish and
 * Withdraw render only in `live` mode — a borrowed (read-only) region view
 * shows the board without write affordances.
 *
 * Design deltas, mocked rather than invented: pay column (BON-245),
 * request counts + Awaiting confirmation / Filled columns (BON-242/243).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { withdrawOpenShift, type ShiftRequester } from '../../../api/openShifts';
import { crewPositionLabel } from '../../../lib/crewPositions';
import { KpiStrip } from '../primitives';

import { BucketError, BucketLoading, SectionCard } from './BucketStates';
import { PublishShiftDrawer } from './PublishShiftDrawer';
import { useRegionalShiftBoard, type OpenShiftBoardEntry } from './regionalApi';
import { formatShiftStart } from './shiftFormat';
import { ShiftRequestsPanel } from './ShiftRequestsPanel';
import { useShiftDecisions } from './useShiftDecisions';

const EM_DASH = '—';

function positionLabel(position: string): string {
  return crewPositionLabel(position);
}

/** "today" / "1d ago" / "12d ago" — the board's Published column. */
export function publishedAge(publishedAtIso: string, now = new Date()): string {
  const days = Math.floor((now.getTime() - new Date(publishedAtIso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  return `${days}d ago`;
}

interface RowProps {
  shift: OpenShiftBoardEntry;
  live: boolean;
  onWithdraw: (id: string) => void;
  withdrawing: boolean;
  requesters: ShiftRequester[];
  expanded: boolean;
  onToggle: () => void;
  deciding: boolean;
  onConfirm: (userId: string) => void;
  onDecline: (userId: string) => void;
}

function ShiftRow(props: RowProps) {
  const { shift, live, onWithdraw, withdrawing, requesters, expanded } = props;
  const waiting = requesters.length;

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'nowrap' }}>{formatShiftStart(shift.startsAtIso)}</td>
        <td>
          <span style={{ fontWeight: 600 }}>{shift.venueShort}</span>
          <span style={{ color: 'var(--nb-subtle)' }}> · {shift.workshopTypeName}</span>
        </td>
        <td>{positionLabel(shift.position)}</td>
        {/* TODO(BON-245): per-position shift pay. */}
        <td className="nb-tabular nb-mono">{EM_DASH}</td>
        <td className="nb-muted">{publishedAge(shift.publishedAtIso)}</td>
        <td>
          {waiting === 0 ? (
            'Open'
          ) : (
            <button
              type="button"
              className="nb-btn nb-btn-ghost"
              aria-expanded={expanded}
              onClick={props.onToggle}
            >
              {waiting} waiting
            </button>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>
          {live && (
            <button
              type="button"
              className="nb-btn nb-btn-ghost"
              disabled={withdrawing}
              onClick={() => onWithdraw(shift.id)}
            >
              Withdraw
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--nb-surface-sunken)', padding: '4px 12px' }}>
            <ShiftRequestsPanel
              requesters={requesters}
              live={live}
              busy={props.deciding}
              onConfirm={props.onConfirm}
              onDecline={props.onDecline}
            />
          </td>
        </tr>
      )}
    </>
  );
}

type TableProps = Omit<
  RowProps,
  'shift' | 'requesters' | 'expanded' | 'onToggle' | 'onConfirm' | 'onDecline'
> & {
  shifts: OpenShiftBoardEntry[];
  pendingRequests: Record<string, ShiftRequester[]>;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onConfirm: (shiftId: string, userId: string) => void;
  onDecline: (shiftId: string, userId: string) => void;
};

function ShiftTable({ shifts, live, onWithdraw, withdrawing, ...rest }: TableProps) {
  if (shifts.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
        No open shifts published. Unstaffed workshops don’t appear here on their own — publish a
        shift to offer a position to your region’s teachers.
      </div>
    );
  }
  return (
    <table className="nb-tbl">
      <thead>
        <tr>
          <th>Date</th>
          <th>Workshop</th>
          <th>Position</th>
          <th>Pay</th>
          <th>Published</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {shifts.map((s) => (
          <ShiftRow
            key={s.id}
            shift={s}
            live={live}
            onWithdraw={onWithdraw}
            withdrawing={withdrawing}
            requesters={rest.pendingRequests[s.id] ?? []}
            expanded={rest.expandedId === s.id}
            onToggle={() => rest.onToggle(s.id)}
            deciding={rest.deciding}
            onConfirm={(userId) => rest.onConfirm(s.id, userId)}
            onDecline={(userId) => rest.onDecline(s.id, userId)}
          />
        ))}
      </tbody>
    </table>
  );
}

export default function RegionalShiftBoard({ regionSlug }: { regionSlug: string }) {
  const qc = useQueryClient();
  const q = useRegionalShiftBoard(regionSlug);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  // One row open at a time — the panel is tall and two expanded rows push the
  // rest of the board off-screen.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const decisions = useShiftDecisions(regionSlug);

  const boardKey = ['dashboards.regional', regionSlug, 'shifts'];
  const withdraw = useMutation({
    mutationFn: withdrawOpenShift,
    onMutate: () => setWithdrawError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: boardKey }),
    onError: (err: Error) => {
      setWithdrawError(err.message);
      // A 409/404 here proves the board is stale (someone else already
      // withdrew or filled it) — refetch so the dead row disappears.
      qc.invalidateQueries({ queryKey: boardKey });
    },
  });

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const live = q.data.mode === 'live';
  const { shifts, pendingRequests, filledCount } = q.data.data;
  // "Awaiting confirmation" counts SHIFTS with someone waiting, not requests —
  // three people on one shift is still one decision for the RD to make.
  const awaiting = shifts.filter((s) => (pendingRequests[s.id]?.length ?? 0) > 0).length;

  return (
    <>
      <KpiStrip
        items={[
          { label: 'Open', value: String(shifts.length) },
          { label: 'Awaiting confirmation', value: String(awaiting) },
          { label: 'Filled', value: String(filledCount) },
        ]}
      />
      <SectionCard
        title="Shift board"
        action={
          live ? (
            <button
              type="button"
              className="nb-btn nb-btn-primary"
              onClick={() => setDrawerOpen(true)}
            >
              Publish shift
            </button>
          ) : undefined
        }
      >
        {(withdrawError ?? decisions.error) && (
          <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
            {withdrawError ?? decisions.error}
          </div>
        )}
        {decisions.warning && (
          <div role="status" style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
            {decisions.warning}
          </div>
        )}
        <ShiftTable
          shifts={shifts}
          live={live}
          onWithdraw={(id) => withdraw.mutate(id)}
          withdrawing={withdraw.isPending}
          pendingRequests={pendingRequests}
          expandedId={expandedId}
          onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
          deciding={decisions.busy}
          onConfirm={decisions.confirm}
          onDecline={decisions.decline}
        />
      </SectionCard>
      {drawerOpen && (
        <PublishShiftDrawer regionSlug={regionSlug} onClose={() => setDrawerOpen(false)} />
      )}
    </>
  );
}
